import { Link } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Footer } from "@/components/Footer";
import { EthicsComplianceBanner } from "@/components/EthicsComplianceBanner";
import { SectionDivider } from "@/components/SectionDivider";
import { ArrowRight } from "lucide-react";

// Below-the-fold sections are deferred into a single lazy chunk so the
// homepage's initial paint only ships the hero, nav, and editorial copy.
// StrategyAssistantCTA also lives here so its framer-motion dependency
// stays out of the initial bundle (it animates a card well below the fold).
const BelowFold = lazy(() => import("@/components/home/BelowFold"));
const StrategyAssistantCTA = lazy(() =>
  import("@/components/StrategyAssistantCTA").then((m) => ({
    default: m.StrategyAssistantCTA,
  })),
);

const EXPLORE_CARDS = [
  {
    title: "Interactive Tools & Assessments",
    blurb:
      "Self-guided calculators and visualizers — buy-sell funding gaps, capital readiness, trust architecture, and succession decision trees.",
    href: "/tools-assessments",
    cta: "Open the tools",
  },
  {
    title: "Methodology & Resources",
    blurb:
      "How SRG works — the methodology, cross-service integration, industry portals, and local expertise behind coordinated strategy.",
    href: "/methodology-resources",
    cta: "See the method",
  },
  {
    title: "Where SRG Fits",
    blurb:
      "The full SRG process: who we serve, what we do, and how strategy, structure, and coordination work as one plan.",
    href: "/where-srg-fits",
    cta: "Explore the process",
  },
  {
    title: "Case Studies & Proof",
    blurb:
      "Real planning scenarios showing how business owners navigated succession, funding, and wealth preservation.",
    href: "/case-studies",
    cta: "Read the outcomes",
  },
  {
    title: "Resource Center",
    blurb:
      "Downloadable guides, checklists, and planning frameworks for business owners and high-net-worth individuals.",
    href: "/resources",
    cta: "Browse resources",
  },
  {
    title: "How We Work",
    blurb:
      "Flat-fee engagements scoped to the work — quarterly and annual corporate reviews that keep the strategy current as the business changes.",
    href: "/pricing",
    cta: "See how we work",
  },
  {
    title: "Expert Insights",
    blurb:
      "Articles on buy-sell agreements, commercial lending, key-person risk, executive benefits, and wealth preservation.",
    href: "/insights",
    cta: "Read insights",
  },
];

const Index = () => {
  return (
    <div className="min-h-screen bg-background font-sans selection:bg-accent/20 selection:text-accent">
      <Navbar />
      <Hero />
      <EthicsComplianceBanner />

      {/* Three Steps — deliberate approach strip */}
      <section className="border-y border-border bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1240px] grid grid-cols-1 lg:grid-cols-[1.1fr_1fr_1fr_1fr] gap-6 py-12">
          <h3 className="font-serif text-2xl lg:text-[28px] text-primary lg:pr-12 lg:border-r lg:border-border">
            A more deliberate approach to business-owner wealth.
          </h3>
          {[
            {
              n: "01",
              t: "Understand",
              p: "The business, ownership, cash flow, obligations, and objectives.",
            },
            {
              n: "02",
              t: "Structure",
              p: "Connect the right financial, legal, and protection disciplines.",
            },
            {
              n: "03",
              t: "Coordinate",
              p: "Work alongside your existing CPA and attorney, with clarity of purpose.",
            },
          ].map((s) => (
            <div key={s.n} className="lg:px-7">
              <div className="font-serif text-[30px] text-accent leading-none">
                {s.n}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-primary mt-3 mb-2.5">
                {s.t}
              </div>
              <p className="text-[13px] text-muted-foreground leading-relaxed">
                {s.p}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* From the Founder — point of view between the approach and the firm */}
      <section className="bg-background py-20 md:py-28 border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <SectionDivider eyebrow="From the Founder" />
          <div className="mt-8">
            <p className="font-serif text-xl md:text-2xl text-primary leading-[1.6] mb-6">
              Your CPA focuses on this year's tax return. Your attorney drafts
              legal documents in isolation. Your commercial lender looks only at
              cash flow. Your advisor focuses primarily on liquid assets.
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-[1.8] mb-6">
              <strong className="text-primary font-semibold">
                But business owners don't live in silos.
              </strong>{" "}
              A decision to expand with commercial debt changes your personal
              guarantee exposure. A change in company valuation changes the
              funding requirements of your buy-sell agreement. The way you
              structure executive benefits changes your estate transfer
              strategy.
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-[1.8] mb-10">
              I founded SRG Consulting PLLC because successful business owners
              need more than fragmented advice.
            </p>
            <div className="flex items-center gap-4 pt-6 border-t border-border">
              <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center font-serif text-lg font-bold shrink-0">
                AD
              </div>
              <div>
                <p className="font-serif text-lg text-primary font-bold leading-tight">
                  Anthony D'Amato
                </p>
                <p className="text-sm text-accent font-medium">
                  Founder &amp; Principal Consultant, SRG Consulting PLLC
                </p>
              </div>
              <Link
                to="/founders-message"
                className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline whitespace-nowrap"
              >
                Read the full message
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Condensed Where SRG Fits teaser */}
      <section className="relative bg-primary text-white py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 texture-paper opacity-30 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-[40%] bg-accent/[0.06] blur-[120px] rounded-full pointer-events-none" />

        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="text-center mb-16">
            <SectionDivider eyebrow="Strategic Planning & Coordination" />
            <h2 className="heading-editorial text-3xl md:text-5xl lg:text-[3.5rem] text-white mb-5">
              Where SRG Fits
              <span className="block text-accent font-normal italic mt-1 shimmer-gold">
                in Your Business
              </span>
            </h2>
            <p className="text-base md:text-lg text-white/50 italic font-serif">
              For Business Owners, Executives, Families, and Individuals
            </p>
          </div>

          <div className="bg-glass-premium rounded-2xl p-8 md:p-12 mb-12 shadow-editorial">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-6 h-px bg-accent/50" />
              <h3 className="text-accent text-[11px] font-semibold tracking-[0.28em] uppercase">
                Who We Are: The Experts Behind The Structure
              </h3>
              <div className="w-6 h-px bg-accent/50" />
            </div>
            <p className="body-editorial text-lg md:text-xl text-white/85 leading-[1.7] mb-8 text-center max-w-4xl mx-auto">
              <strong className="text-white font-semibold">
                SRG Consulting PLLC is the financial expert, the insurance
                expert, and the structural expert
              </strong>{" "}
              behind trusts, corporations, and how they interact with one
              another. We build business models, run feasibility studies, and
              engineer continuity throughout the entire course and lifetime of
              your business.
            </p>
            <div className="relative bg-accent/[0.07] border-l-2 border-accent rounded-r-lg p-6 md:p-7 max-w-3xl mx-auto">
              <p className="text-base text-white/75 leading-relaxed">
                <strong className="text-accent">
                  The only things we don't physically do
                </strong>{" "}
                are prepare your taxes and draft the physical trust document —
                your CPA and attorney handle those.
                <span className="text-white font-medium">
                  {" "}
                  We do everything else: the strategy, the structure, the
                  analysis, the funding, and the coordination that makes all of
                  it work as one plan.
                </span>
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/where-srg-fits">
              <button className="highlight-gold px-9 py-4 rounded-full text-base font-bold transition-all duration-300 shadow-editorial hover:scale-[1.02] hover:shadow-accent/30">
                Explore the Full SRG Process →
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Editorial transition */}
      <div className="relative h-24 md:h-32 bg-gradient-to-b from-primary via-primary/55 to-background pointer-events-none">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-accent/25 to-transparent" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <Suspense fallback={null}>
          <StrategyAssistantCTA />
        </Suspense>
      </div>

      {/* Below-the-fold sections — deferred into one lazy chunk */}
      <Suspense fallback={null}>
        <BelowFold />
      </Suspense>

      {/* Explore More — links to the moved sections on dedicated pages */}
      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1240px]">
          <div className="text-center mb-12">
            <SectionDivider eyebrow="Explore Further" />
            <h2 className="heading-editorial text-3xl md:text-4xl text-primary mt-5">
              Go deeper into any area
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              The home page is the overview. Each area below has its own page
              with the full detail, tools, and resources.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXPLORE_CARDS.map((card, i) => (
              <div
                key={card.href}
                style={{
                  animation: `homeCardIn 0.5s ease-out ${i * 0.08}s both`,
                }}
              >
                <Link
                  to={card.href}
                  className="group bg-card border border-border rounded-2xl p-7 flex flex-col h-full transition-all duration-300 hover:border-accent/40 hover:-translate-y-1 hover:shadow-editorial"
                >
                  <h3 className="font-serif text-xl text-primary mb-3">
                    {card.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-grow">
                    {card.blurb}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">
                    {card.cta}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
